import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { authService } from '../services/authService';
import type { PortalContact, PortalLoginResponse, DiscoveredTenant, DomainResolutionResult } from '../services/authService';
import { setSentryUser, clearSentryUser } from '../lib/sentry';
import { AuthContext } from './authContextDef';

const KNOWN_DOMAINS = ['destekgor.com', 'localhost'];

function isKnownDomain(hostname: string): boolean {
  return KNOWN_DOMAINS.some(d => hostname === d || hostname.endsWith(`.${d}`));
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [contact, setContact] = useState<PortalContact | null>(null);
  const [discoveredTenant, setDiscoveredTenant] = useState<DiscoveredTenant | null>(null);
  const [resolvedBranding, setResolvedBranding] = useState<DomainResolutionResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      // Check if we're on a custom domain
      const hostname = window.location.hostname;
      if (!isKnownDomain(hostname)) {
        const resolved = await authService.resolveCustomDomain(hostname);
        if (resolved) {
          setResolvedBranding(resolved);
          localStorage.setItem('portal_tenant_slug', resolved.tenantSlug);

          // Set dynamic favicon
          if (resolved.faviconUrl) {
            let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
            if (!link) {
              link = document.createElement('link');
              link.rel = 'icon';
              document.head.appendChild(link);
            }
            link.href = resolved.faviconUrl;
          }
        }
      }

      const storedContact = localStorage.getItem('portal_contact');
      const accessToken = localStorage.getItem('portal_access_token');

      if (storedContact && accessToken) {
        try {
          const currentContact = await authService.getCurrentContact();
          setContact(currentContact);
          localStorage.setItem('portal_contact', JSON.stringify(currentContact));
        } catch {
          // Token expired or invalid
          localStorage.removeItem('portal_access_token');
          localStorage.removeItem('portal_refresh_token');
          localStorage.removeItem('portal_contact');
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const discoverTenant = async (email: string): Promise<DiscoveredTenant | null> => {
    const result = await authService.discoverTenant(email);
    if (result) {
      setDiscoveredTenant(result);
    }
    return result;
  };

  const clearDiscoveredTenant = () => {
    setDiscoveredTenant(null);
    localStorage.removeItem('portal_tenant_slug');
  };

  const login = async (email: string, password: string) => {
    const tenantSlug = localStorage.getItem('portal_tenant_slug');
    if (!tenantSlug) {
      throw new Error('Tenant slug not set');
    }

    const response: PortalLoginResponse = await authService.login(email, password, tenantSlug);

    localStorage.setItem('portal_access_token', response.accessToken);
    localStorage.setItem('portal_refresh_token', response.refreshToken);

    const contactData: PortalContact = {
      contactId: response.contactId,
      email: response.email,
      firstName: response.firstName,
      lastName: response.lastName,
      fullName: response.fullName,
      customerId: response.customerId,
      customerName: response.customerName,
      companyName: response.companyName,
      roleName: response.roleName,
      permissions: response.permissions,
    };

    localStorage.setItem('portal_contact', JSON.stringify(contactData));
    setContact(contactData);
    // Plan PII kuralı: email/name yollamıyoruz; sadece id + role + tenantSlug.
    setSentryUser({
      id: contactData.contactId,
      role: contactData.roleName,
      tenantSlug: tenantSlug ?? undefined,
    });
  };

  const logout = async () => {
    await authService.logout();
    setContact(null);
    clearSentryUser();
  };

  const hasPermission = (permission: string): boolean => {
    if (!contact) return false;
    return contact.permissions.includes(permission);
  };

  return (
    <AuthContext.Provider
      value={{
        contact,
        discoveredTenant,
        resolvedBranding,
        isLoading,
        isAuthenticated: !!contact,
        discoverTenant,
        login,
        logout,
        clearDiscoveredTenant,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
